import { useEffect, useState } from 'react';
import { tokenValidator } from '@/core/utils/tokenValidator';
import { toast } from 'react-toastify';
import { refreshAccessToken } from '@/core/utils/auth0';

interface UseTokenExpiryOptions {
  warningMinutes?: number;
  autoRefresh?: boolean;
  onExpirySoon?: () => void;
  onExpired?: () => void;
}

export const useTokenExpiry = (options: UseTokenExpiryOptions = {}) => {
  const {
    warningMinutes = 5,
    autoRefresh = true,
    onExpirySoon,
    onExpired,
  } = options;

  const [timeUntilExpiry, setTimeUntilExpiry] = useState<number | null>(null);
  const [hasShownWarning, setHasShownWarning] = useState(false);

  useEffect(() => {
    // Check token expiry every 30 seconds
    const checkExpiry = () => {
      const time = tokenValidator.getTimeUntilExpiry();
      setTimeUntilExpiry(time);

      if (time === null) {
        // No token
        return;
      }

      if (time <= 0) {
        // Token expired
        onExpired?.();
        return;
      }

      // Check if expiring soon
      if (tokenValidator.willExpireSoon(warningMinutes) && !hasShownWarning) {
        setHasShownWarning(true);
        
        const minutes = Math.ceil(time / 60);
        toast.warning(
          `Votre session expire dans ${minutes} minute${minutes > 1 ? 's' : ''}`,
          { autoClose: 5000 }
        );
        
        onExpirySoon?.();

        // Auto-refresh if enabled
        if (autoRefresh) {
          console.log('🔄 Auto-refreshing token before expiry...');
          refreshAccessToken()
            .then(() => {
              toast.success('Session renouvelée automatiquement');
              setHasShownWarning(false); // Reset warning flag
            })
            .catch((error) => {
              console.error('Failed to auto-refresh token:', error);
              toast.error('Impossible de renouveler la session. Veuillez vous reconnecter.');
            });
        }
      }
    };

    // Initial check
    checkExpiry();

    // Set up interval
    const interval = setInterval(checkExpiry, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [warningMinutes, autoRefresh, hasShownWarning, onExpirySoon, onExpired]);

  return {
    timeUntilExpiry,
    isExpiringSoon: tokenValidator.willExpireSoon(warningMinutes),
    isValid: tokenValidator.isTokenValid(),
  };
};
